import moment from 'moment';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table,
  Typography,
  Tag,
  Popover,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { ApplicantStatusesEnum } from '@thinknet-gmbh/platform-common';

import { formatByMask } from 'src/common/helpers/common.helpers';
import { IApplicantsListTableProps } from 'src/pages/vacancies/sub-pages/vacancy-details/components/applicants/applicants.interfaces';
import { VACANCY_DETAILS_TABS } from 'src/pages/vacancies/sub-pages/vacancy-details/vacancy-details.constants';

import { APPLICANTS_STATUSES_STYLES } from './applicants-list-table.constants';
import { AttachmentWrapper } from './applicants-list-table.styles';

const ApplicantsListTable = ({
  dataSource,
  paginationConfig,
  onTableChange,
}: IApplicantsListTableProps) => {
  const { t } = useTranslation(['common', 'vacancies']);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Table
      t={t}
      dataSource={dataSource}
      pagination={paginationConfig}
      rowKey={record => record._id}
      onChange={onTableChange}
      onRow={record => ({
        onClick: () =>
          navigate(
            `${location.pathname}?type=${VACANCY_DETAILS_TABS.applicantDetails}&applicantId=${record._id}`
          ),
      })}
    >
      <Table.Column
        title={t('common:labels.fullName')}
        render={applicant => (
          <Typography>{`${applicant.firstName} ${applicant.lastName}`}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.email')}
        dataIndex="email"
        key="email"
        render={email => <Typography>{email}</Typography>}
      />
      <Table.Column
        title={t('vacancies:labels.phone')}
        dataIndex="phoneNumber"
        key="phoneNumber"
        render={phoneNumber => (
          <Typography>{formatByMask(phoneNumber, '+## ### ### #######')}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.status')}
        dataIndex="status"
        key="status"
        render={status => {
          const { background, fontColor } =
            APPLICANTS_STATUSES_STYLES[status as ApplicantStatusesEnum];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(`vacancies:labels.applicantsStatuses.${camelCase(status.toLowerCase())}`)}
            </Tag>
          );
        }}
      />
      <Table.Column
        title={t('vacancies:labels.attachment')}
        dataIndex="documents"
        key="documents"
        render={({ resumeFiles }) =>
          resumeFiles.map((file: any) => (
            <Tag key={file._id} background={PLATFORM_THEME_COLORS.grayVariations.light}>
              <AttachmentWrapper>
                <Icon icon={ICONS.file} color={PLATFORM_THEME_COLORS.primary.grey} size={16} />
                <Typography
                  size={TYPOGRAPHY_SIZE.small}
                  fontColor={PLATFORM_THEME_COLORS.primary.grey}
                >
                  {file.filename}
                </Typography>
              </AttachmentWrapper>
            </Tag>
          ))
        }
      />
      <Table.Column
        title={t('vacancies:labels.applicationDate')}
        dataIndex="createdAt"
        key="createdAt"
        sorter
        render={createdAt => <Typography>{moment(createdAt).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        title={t('common:labels.action')}
        width={50}
        render={({ _id }) => (
          <Popover
            elements={[
              {
                label: t('employee:labels.viewDetails'),
                icon: ICONS.foodMenu,
                key: ICONS.foodMenu,
                handler: () =>
                  navigate(
                    `${location.pathname}?type=${VACANCY_DETAILS_TABS.applicantDetails}&applicantId=${_id}`
                  ),
              },
            ]}
          >
            <Icon
              icon={ICONS.dotsVerticalRounded}
              size={22}
              color={PLATFORM_THEME_COLORS.primary.grey}
              rightMargin={false}
            />
          </Popover>
        )}
      />
    </Table>
  );
};

export default ApplicantsListTable;
